/**
 * Initials (docs/spec/04 §6): a score made the table. Three letters arcade style - up/down cycle,
 * left/right move, fire confirms, or just type - then on to the table with the new line flashing.
 */

import { dateKey } from '../../game/daily';
import type { Screen } from '../../render/screen';
import { badgesFor, drawSprite } from '../../render/sprites';
import type { SpriteId } from '../../render/sprites';
import { UiScene } from '../kit/base';
import { drawButton } from '../kit/chrome';
import type { EditorLook } from '../kit/initialsEditor';
import { drawEditor, editorPointer, rememberedInitials, rememberInitials } from '../kit/initialsEditor';
import type { InitialsState, InitialsStep } from '../kit/initialsModel';
import { initialsAction, initialsKey, startInitials } from '../kit/initialsModel';
import { notOurs } from '../kit/input';
import type { Rect } from '../kit/menuModel';
import { inRect } from '../kit/menuModel';
import { commas, ordinal } from '../kit/text';
import { ACCENT, CX, cycle, INK, MUTED, RAINBOW } from '../kit/theme';
import type { KeyInput, MenuAction, PendingScore, PointerInput, SceneHost } from '../scene';
import { TABLE_TABS } from './hiscoresModel';

const EDITOR: EditorLook = { cx: CX, y: 128, scale: 3 };
const DONE: Rect = { x: 72, y: 184, w: 80, h: 18 };
const BADGES_Y = 228;
/** No more badges than fit between the margins. */
const MAX_BADGES = 20;

export class InitialsScene extends UiScene {
  protected readonly heading = 'NEW HIGH SCORE!';
  protected override readonly track = 'hiscore' as const;
  protected override readonly showBack = false;
  private s: InitialsState = startInitials(rememberedInitials());
  private doneHot = false;
  private sent = false;

  constructor(host: SceneHost, private readonly entry: PendingScore) {
    super(host);
  }

  private get tableName(): string {
    return TABLE_TABS.find((t) => t.id === this.entry.table)?.label ?? '';
  }

  protected override describe(): string {
    const e = this.entry;
    const what = `${e.score} points, ${this.tableName}, stage ${e.stage}`;
    return `New high score. ${what}. Enter your initials: ${this.s.letters}`;
  }

  protected override hint(): string {
    return `▲▼ LETTER ◄► MOVE ${this.words().ok} OK`;
  }

  protected override touchHint(): string {
    return 'TAP ▲ OR ▼, THEN DONE';
  }

  protected paint(screen: Screen): void {
    const e = this.entry;
    screen.text('YOUR SCORE', CX, 36, MUTED, 'center');
    const text = commas(e.score);
    [...text].forEach((ch, i) => {
      const x = CX - text.length * 8 + i * 16;
      screen.text(ch, x, 48, cycle(RAINBOW, this.ms, 90, -i), 'left', 2);
    });
    screen.text(`${this.tableName} · STAGE ${e.stage}`, CX, 72, INK, 'center');
    const rank = this.services.scores.rank(e.table, e.score);
    if (rank >= 0) screen.text(`${ordinal(rank + 1)} PLACE`, CX, 84, ACCENT, 'center');
    screen.text('ENTER YOUR INITIALS', CX, 100, MUTED, 'center');
    drawEditor(screen, this.s, EDITOR, this.ms);
    drawButton(screen, DONE, 'DONE', this.doneHot ? 'selected' : 'idle', this.ms);
    // The stage reached, in the cabinet's own badges.
    const badges = badgesFor(e.stage).slice(0, MAX_BADGES);
    const x0 = CX - (badges.length * 10 - 2) / 2;
    badges.forEach((v, i) => drawSprite(screen, `badge${v}` as SpriteId, 0, x0 + i * 10 + 4, BADGES_Y));
  }

  private apply(step: InitialsStep): void {
    this.s = step.state;
    if (step.sound) this.sound(step.sound);
    if (step.done) this.submit();
    else if (step.sound === 'type' || step.sound === 'move') this.say(this.s.letters[this.s.cursor] ?? '');
  }

  protected onAction(a: MenuAction): void {
    this.apply(initialsAction(this.s, a));
  }

  /** There is no leaving without a name: back steps back a letter. */
  protected override back(): void {
    this.apply(initialsAction(this.s, 'back'));
  }

  keydown(k: KeyInput): boolean {
    // Ctrl+R is a reload, not an R.
    if (notOurs(k)) return false;
    const step = initialsKey(this.s, k.code, k.key);
    if (!step) return false;
    this.apply(step);
    return true;
  }

  protected override onPointer(p: PointerInput): void {
    this.doneHot = inRect(DONE, p.x, p.y);
    if (p.kind === 'down' && p.button !== 0) return;
    if (p.kind === 'down' && this.doneHot) {
      this.sound('select');
      return this.submit();
    }
    const step = editorPointer(this.s, EDITOR, p.kind, p.x, p.y, p.delta);
    if (step) this.apply(step);
  }

  private submit(): void {
    if (this.sent) return;
    this.sent = true;
    const e = this.entry;
    rememberInitials(this.s.letters);
    const rank = this.services.scores.add(e.table, {
      initials: this.s.letters,
      score: e.score,
      stage: e.stage,
      date: dateKey(this.services.today()),
    });
    if (rank >= 0) this.host.go({ to: 'hiscores', table: e.table, highlight: rank });
    else this.host.go({ to: 'hiscores', table: e.table });
  }
}
